import React, { FC } from 'react';
import styled from 'styled-components';
import RaceView from '../../components/shared/RaceView';
import AotdEmojiQuiz from '../quiz/AotdEmojiQuiz';
import CliqueAttribution from '../../components/shared/CliqueAttribution';
import AotdPrintsLine from '../../../prints/AotdPrintsLine';
import type { CliqueOrigin, RaceData } from '../../api/types';

interface AuthorViewTime {
    viewedAt: string;
    offsetMs: number;
    offsetFormatted: string;
    rank?: number;
    totalRanked?: number;
    yearRank?: number;
    yearTotalRanked?: number;
}

interface AOTDStatsProps {
    generationElapsedTime?: string;
    generationTimeMs?: number;
    nextAOTDEmojis?: string[];
    views?: {
        verified: number;
        total: number;
    };
    hasPermission?: boolean;
    verifier?: React.ReactNode;
    authorViewTime?: AuthorViewTime;
    createdBy?: string;
    /** Clique origin, when the day was initialized from an x-n450s site. */
    origin?: CliqueOrigin;
    race?: RaceData;
    /** True when this day was generated under birthday priority. Adds a pink
     *  "with birthday priority" note to the author view-time line. */
    isBirthday?: boolean;
}

const StatsContainer = styled.div`
  margin-top: 15px;
  font-size: 0.75rem;
  color: #666;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
`;

function formatOffsetDuration(offsetMs: number): string {
    const sign = offsetMs < 0 ? '-' : '+';
    const totalSeconds = Math.abs(offsetMs) / 1000;
    if (totalSeconds < 60) {
        return `${sign}${totalSeconds.toFixed(2)}s`;
    }
    const roundedSeconds = Math.round(totalSeconds);
    const hours = Math.floor(roundedSeconds / 3600);
    const minutes = Math.floor((roundedSeconds % 3600) / 60);
    const seconds = roundedSeconds % 60;
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    return `${sign}${parts.join(' ')}`;
}

function getOrdinalSuffix(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function getRankColor(rank: number): string {
    if (rank <= 3) return '#9333ea'; // purple
    if (rank <= 10) return '#22c55e'; // green
    return '#9ca3af'; // gray
}

function getOffsetColor(offsetMs: number): string {
    const seconds = offsetMs / 1000;
    if (seconds <= 2) return '#9333ea'; // purple
    if (seconds <= 10) return '#22c55e'; // green
    return '#9ca3af'; // gray
}

const AOTDStats: FC<AOTDStatsProps> = ({
    generationElapsedTime,
    generationTimeMs,
    nextAOTDEmojis,
    views,
    hasPermission,
    verifier,
    authorViewTime,
    createdBy,
    origin,
    race,
    isBirthday
}) => {
    // race is meaningful if author exists and at least 1 other participant exists
    const hasRaceData = !!(race && race.author && race.participants && race.participants.length >= 1);

    const hasStats = generationElapsedTime || (nextAOTDEmojis && nextAOTDEmojis.length > 0) || views !== undefined || verifier || authorViewTime || hasRaceData;
    if (!hasStats) return null;

    return (
        <StatsContainer>

            {generationElapsedTime && (
                <StatItem>
                    <span className="text-xs text-gray-400">
                        <span className="italic">generated in</span> <b>{generationElapsedTime}</b>
                    </span>
                </StatItem>
            )}

            {authorViewTime && createdBy && (
                <StatItem>
                    <span className="text-xs text-gray-400">
                        <b><CliqueAttribution origin={origin} fallback={createdBy} /></b>{' '}
                        <span className="italic">got it</span>{' '}
                        <b style={{ color: getOffsetColor(authorViewTime.offsetMs) }}>{formatOffsetDuration(authorViewTime.offsetMs)}</b>{' '}
                        <span className="italic">after aotd unlocked</span>
                        {isBirthday && (
                            <>
                                {' '}
                                <span className="italic" style={{ color: '#be185d' }}>
                                    🎂 with birthday priority
                                </span>
                            </>
                        )}
                    </span>
                </StatItem>
            )}

            {(authorViewTime?.rank != null || authorViewTime?.yearRank != null) && (
                <StatItem>
                    <span className="text-xs text-gray-400">
                        {authorViewTime?.rank != null && (
                            <>
                                <b style={{ color: getRankColor(authorViewTime.rank) }}>
                                    {getOrdinalSuffix(authorViewTime.rank)}
                                </b>{' '}
                                <span className="italic">fastest aotd ever</span>
                            </>
                        )}
                        {authorViewTime?.rank != null && authorViewTime?.yearRank != null && (
                            <span className="italic">{' · '}</span>
                        )}
                        {authorViewTime?.yearRank != null && (
                            <>
                                <b style={{ color: getRankColor(authorViewTime.yearRank) }}>
                                    {getOrdinalSuffix(authorViewTime.yearRank)}
                                </b>{' '}
                                <span className="italic">this year</span>
                            </>
                        )}
                    </span>
                </StatItem>
            )}

            {views && views.total > 0 && views.verified > 0 && (
                <StatItem>
                    <span className="text-xs text-gray-400">
                        <b>{views.verified}</b> <span className="italic">user view{views.verified === 1 ? '' : 's'}</span>
                        {/* (<b>{views.total}</b> <span className="italic">total</span>) */}
                    </span>
                </StatItem>
            )}

            <AotdPrintsLine />

            {verifier && (
                <StatItem style={{ width: '100%', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {verifier}
                </StatItem>
            )}

            {hasRaceData && race && (
                <RaceView race={race} generationTimeMs={generationTimeMs} />
            )}

            {createdBy && <AotdEmojiQuiz author={createdBy} />}

            {hasPermission && nextAOTDEmojis && nextAOTDEmojis.length > 0 && (
                <StatItem>
                    <span className="text-gray-400 flex items-center">
                        <span className="text-xs italic">your next AOTD emojis:</span>
                        <span className="font-bold text-base ml-1 leading-none">{nextAOTDEmojis?.join(' ')}</span>
                    </span>
                </StatItem>
            )}
        </StatsContainer>
    );
};

export default AOTDStats;
